import { Component, EventEmitter, Input, Output } from "@angular/core";
import { AssigneeRole } from "@core/models/main-filter-view";
import { BaseSortingFilter } from "src/app/services/companies-search/saved-search.service";
import { DCCompanyType } from "../../../services/companies/companies.service";
import { GlobalModalService } from "@core/services/global-modal.service";
import { Company } from "@core/models/company";
import { AuthService } from "@core/auth/auth.service";
import { ModalUtilsService } from "src/app/services/modal-utils.service";
import { RouterService } from "src/app/services/router/router.service";
import { Nullable } from "primeng/ts-helpers";
import { ModalActions } from "@core/models/types";
import { InteractionType, WorkFlow, WorkFlowStatusId } from "@core/models/auxiliary-company-models";
import { getActiveICVotingWorkflow, getActiveOrLastICVotingWorkflow, getVotingBackgroundClasses } from "src/app/utils/company-utils";
import { CommentService } from "@core/services/comment.service";
import { convertCronExpressionToTime } from "src/app/utils/utils";
import moment from "moment";

export interface ColumnMapping {
  header: string;
  mappedTo: string;
  case?: string;
  showHeader?: boolean;
  sortable?: boolean;
  sortProperty?: string;
  sortDirection?: string;
  editable?: boolean;
  editAction?: Nullable<ModalActions>;
  openLink?: string;
  childTab?: Number;
}

interface DataPaging {
  pageNumber?: number;
  pageSize?: number;
  thenSortDirection?: string;
  thenSortProperty?: string;
}

interface FilterFields {
  isActive?: string,
  hasNotificationTriggers?: string,
  statusesInclude?: number[];
  assigneesInclude?: number[];
  interactionTypes?: number[];
  assigneeRole?: AssigneeRole;
  dcCompanyTypes?: DCCompanyType | number[];
  interactionInitiatorsInclude?: number[];
  isMyElevation?: boolean;
  tagsInclude?: number[];
  companyName?: string[];
  userIds?: number[];
  days?: number;
  showAllUsers?: string;
  companiesInclude?: number[];
  rawValueMapping?: string;
  userId?: number;
}

export interface DataFilter extends DataPaging, BaseSortingFilter, FilterFields {}

@Component({
  selector: "bpc-dynamic-table",
  templateUrl: "./dynamic-table.component.html",
  styleUrl: "./dynamic-table.component.scss",
})
export class DynamicTableComponent {
  @Input() data: any[] = []; // The data to be displayed
  @Input() columns: ColumnMapping[] = []; // The columns to be displayed
  @Input() totalCount: number = 10;
  @Input() pageSize = 100;
  @Input() loading = false;
  @Input() DataFilter!: DataFilter;
  @Input() tableClass!: Nullable<string>;

  @Output() onColumnSortClickEmitter = new EventEmitter<ColumnMapping>();

  skeletonRowCount: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  sortField: string = ""; // For sorting field
  pagination = false; // To hide pagination if needed

  @Input() enableSelection = false; // Enables checkbox selection
  @Output() onSelectionChange = new EventEmitter<any[]>(); // Emits when selection changes
  selectedRows: any[] = []; // Stores selected rows

  constructor(
    private authService: AuthService,
    private globalModalService: GlobalModalService,
    private modalUtilsService: ModalUtilsService,
    private routerService: RouterService,
    private commentService: CommentService
  ) {}

  get Array(): ArrayConstructor {
    return Array;
  }

  get isInvestmentCommitteeAdmin() {
    return this.authService.investmentcommitteeadmin;
  }

  get interactionType(): typeof InteractionType {
    return InteractionType;
  }

  get companyId(){
    return this.globalModalService.companyId;
  }

  ngOnInit(): void {}

  onSelectionChangeEvent(rows: any) {
    this.onSelectionChange.emit(rows);
  }

  // Sorting function
  onSort(event: any) {
    this.sortField = event.field;
  }

  onColumnSortClick(column: ColumnMapping) {
    this.onColumnSortClickEmitter.emit(column);
  }

  getSortClass(column: ColumnMapping) {
    if (this.DataFilter?.sortProperty === column.sortProperty){
      switch (this.DataFilter.sortDirection?.toLowerCase()) {
        case "asc":
          return "fa-sort-down";
        case "desc":
          return "fa-sort-up";
        default:
          return "fa-sort";
      }
    }
    else{
      return "fa-sort";
    }
  }

  onDetailsClick(companyId: string) {
    this.globalModalService.openCompanyModal(companyId);
  }

  onEditClick(columm: ColumnMapping, company: Company){
    let modalParameters = this.modalUtilsService.modalParameters;
    modalParameters.selectedCompany = company;
    modalParameters.currentTabIndex = this.routerService.currentTabIndex;
    modalParameters.currentChildIndex = 0;
    modalParameters.coverageUsers = [];
    company.coverageUsers?.forEach((coverageUser) => {
      modalParameters.coverageUsers?.push(coverageUser);
    });
    modalParameters.teamMembers = [];
    modalParameters.investors = company.investors ?? [];
    modalParameters.previousInvestors = company.investorsDetails;
    modalParameters.nextStep = company.nextStep!;
    modalParameters.currentModalAction = columm.editAction!;
    modalParameters.outreachOwners = [company.outreachOwner ?? undefined, company.outreachPrimaryBackup ?? undefined, company.outreachSecondaryBackup ?? undefined];
    modalParameters.v2 = true;
    modalParameters.targetFinancials = company.targetFinancials;
    modalParameters.previousTargetFinancials = company.targetFinancials;
    modalParameters.totalPIC = company.totalPIC;
    modalParameters.previousTotalPIC = company.totalPIC;
    modalParameters.hC1Y2Y = company.hC1Y2Y;
    modalParameters.previousHC1Y2Y = company.hC1Y2Y;
    modalParameters.outreachOwnerIndex = 0;
    modalParameters.elevationOwner = company.elevationOwner;

    if (this.isInvestmentCommitteeAdmin && columm?.editAction != null && columm?.editAction == "editICDateAdded") {
      modalParameters.selectedCompany = company;
      const voteWorkflow = getActiveICVotingWorkflow(
        company.workflows,
        columm.childTab === 0 ? WorkFlowStatusId.InReview : WorkFlowStatusId.Complete,
      );
      if (voteWorkflow) {
        modalParameters.dateAdded = moment(voteWorkflow!.votingDate!).toDate();
        modalParameters.companyWorkFlowId = voteWorkflow!.id!;
        this.modalUtilsService.toggalModal.next(modalParameters);
      }
    }
    
    
    this.modalUtilsService.toggalModal.next(modalParameters);
  }

  showComments(company: Company){
    this.commentService.showComments(company);
  }

  updateCronJob(company: any) {
    let modalParameters = this.modalUtilsService.modalParameters;
    modalParameters.currentModalAction = "updateCronJob";
    modalParameters.selectedJobId = company.id;
    const convertedExpression = convertCronExpressionToTime(company);
    modalParameters.frequency = convertedExpression.frequency || undefined;
    modalParameters.selectedDayOfWeek = convertedExpression.selectedDayOfWeek || undefined;
    modalParameters.selectedDayOfMonth = convertedExpression.selectedDayOfMonth || undefined;
    modalParameters.selectedTime = convertedExpression.selectedTime;
    this.modalUtilsService.toggalModal.next(modalParameters);
  }

}
